import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightManageUser from '../../assets/landing_page_images/Light-ManageUser.webp';
import DarkManageUser from '../../assets/landing_page_images/Dark-ManageUser.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import { APP_URLS } from '../../helpers/url_helper';
import './ContractorManagementSoftware.css';

const HELP_AREAS = [
  'Contractor and worker compliance',
  'Training and inductions',
  'Employee and contractor documents',
  'Digital forms and inspections',
  'WHS documentation',
  'Sites and site-specific records',
  'Tasks and corrective actions',
  'Audits and compliance evidence',
  'Plant and asset registers',
  'Management reporting',
];

const COMPLIANCE_FEATURES = [
  {
    title: 'Keep Contractor Records Organised',
    paragraphs: [
      "Contractor and worker information can be managed as part of the platform's employee and role management functionality. Employee profiles can include documents and submitted form history, helping teams keep important records connected to the right person.",
    ],
  },
  {
    title: 'Manage Training and Inductions',
    paragraphs: [
      "Training and inductions are part of Complimate's supported compliance workflows. Digital forms can also be used for processes such as site inductions and toolbox talks, including QR-based access for workers in the field.",
    ],
  },
  {
    title: 'Connect Contractors With Sites',
    paragraphs: [
      'Complimate includes a site register for physical locations, with site-level documents and linked digital form submissions.',
      <>
        Manage contractor requirements as part of your broader{' '}
        <Link
          to={APP_URLS.CONSTRUCTION_SAFETY_COMPLIANCE}
          className="section-description-link"
        >
          construction safety compliance
        </Link>
        {' '}process.
      </>,
    ],
  },
  {
    title: 'Track Documents and Expiry Dates',
    paragraphs: [
      'The document management system provides a central location for policies, procedures, SWMS, registers, WHS plans and compliance records. Documents can include review and expiry dates, with filters for expired and soon-to-expire records.',
      'This is useful when contractor-related documentation needs to be reviewed or renewed.',
    ],
  },
];

const AUDIENCE = [
  'WHS and HSE managers',
  'Compliance officers',
  'Quality managers',
  'Site supervisors',
  'Operations managers',
  'Businesses managing multiple sites',
  'Consultants supporting client compliance',
];

const FAQ_ITEMS = [
  {
    question: 'What is contractor management software?',
    answer:
      'Contractor management software helps businesses organise contractor information, documentation, compliance activities and related responsibilities. Complimate connects contractor and worker compliance with broader WHS workflows.',
  },
  {
    question: 'Can Complimate manage contractor documents?',
    answer:
      'Complimate provides document management functionality for WHS and compliance records, including document versions, review dates, expiry dates and searchable storage.',
  },
  {
    question: 'Can contractors complete digital forms?',
    answer:
      "Complimate's Digital Forms system supports mobile form completion, signatures, QR codes and submission records. Forms can be linked to sites, projects and employees.",
  },
  {
    question: 'Can contractor management be connected to site compliance?',
    answer:
      'Yes. Complimate includes a site register with site-level documents and linked digital form submissions, allowing compliance information to be organised by location.',
  },
  {
    question: 'Can Complimate track contractor compliance issues?',
    answer:
      "Complimate's projects and tasks functionality supports assigned tasks, due dates, attachments, comments and evidence, allowing compliance issues and corrective actions to be tracked through to completion.",
  },
  {
    question: 'Is Complimate only for contractors?',
    answer:
      'No. Contractor management is one part of the broader platform. Complimate also supports documents, digital forms, audits, corrective actions, tasks, sites, assets, employee management and other WHS workflows.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/contractor-management/');

function CheckIcon() {
  return (
    <span className="cms-check" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3.75 9.75L7.125 13.125L14.25 4.875"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const ICON_PATHS = {
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6" />
      <path d="M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  training: (
    <>
      <path d="m3 8 9-5 9 5-9 5-9-5Z" />
      <path d="M7 11v5c2.8 2 7.2 2 10 0v-5" />
      <path d="M21 8v6" />
    </>
  ),
  document: (
    <>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h6" />
    </>
  ),
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 3.5h6V7H9zM9 12h6M9 16h6" />
      <path d="m7.5 12 1 1 1.5-2M7.5 16l1 1 1.5-2" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />,
  site: (
    <>
      <path d="M4 21V8l8-5 8 5v13" />
      <path d="M9 21v-6h6v6M8 10h2M14 10h2" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="17" rx="2" />
      <path d="M9 4V2h6v2M8 10l1.5 1.5L12 8.5M14 10h3M8 16l1.5 1.5L12 14.5M14 16h3" />
    </>
  ),
  audit: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 4.5 4.5M8 10.5l1.6 1.6 3.4-3.6" />
    </>
  ),
  plant: (
    <>
      <path d="M4 20V9h10v11M14 13h4l2 3v4h-6" />
      <circle cx="8" cy="20" r="2" />
      <circle cx="17" cy="20" r="2" />
      <path d="M7 9V5h5v4" />
    </>
  ),
  report: (
    <>
      <path d="M5 21V3h14v18H5Z" />
      <path d="M9 17v-4M12 17V9M15 17v-6M8 7h8" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" />
      <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M7 3v4M17 3v4M3 10h18M8 14h3M13 14h3M8 18h3" />
    </>
  ),
};

const HELP_ICONS = [
  'users',
  'training',
  'document',
  'form',
  'shield',
  'site',
  'task',
  'audit',
  'plant',
  'report',
];

const FEATURE_ICONS = ['users', 'training', 'site', 'calendar'];

function FeatureIcon({ name }) {
  return (
    <span className="cms-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.shield}
      </svg>
    </span>
  );
}

function ContractorManagementSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="cms-page">
        <section className="cms-hero">
          <Container>
            <div className="cms-hero-grid">
              <div className="cms-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="shield" />
                  Contractor Management Software
                </p>
                <h1 className="seo-h1">
                  Contractor Management Software for WHS Compliance
                </h1>
                <p className="seo-lead">
                  Managing contractors means keeping track of documents,
                  responsibilities, inductions, forms and compliance records across
                  your business. Complimate contractor management software brings
                  these activities into one connected WHS and compliance platform.
                </p>
                <p className="seo-body">
                  Complimate helps businesses manage contractor requirements as part
                  of their wider{' '}
                  <Link
                    to={APP_URLS.WHS_COMPLIANCE_SOFTWARE}
                    className="section-description-link"
                  >
                    WHS compliance software
                  </Link>
                  {' '}platform.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us"
                    className="btn-teal cms-primary-button"
                    id="cms_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>

              <div className="cms-hero-visual" aria-hidden="true">
                <div className="cms-visual-glow" />
                <div className="cms-product-window">
                  <div className="cms-window-bar"><span /><span /><span /></div>
                  <img
                    src={mode === 'dark' ? DarkManageUser : LightManageUser}
                    alt=""
                  />
                </div>
                <div className="cms-float-card cms-float-card-top">
                  <FeatureIcon name="document" />
                  <span className="cms-visual-lines"><i /><i /><i /></span>
                  <CheckIcon />
                </div>
                <div className="cms-float-card cms-float-card-bottom">
                  <FeatureIcon name="users" />
                  <span className="cms-avatar-stack"><i /><i /><i /></span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section cms-intro-section">
          <Container>
            <div className="cms-intro-card">
              <div className="cms-section-marker"><FeatureIcon name="link" /></div>
              <div>
                <h2 className="seo-h2">What Is Contractor Management Software?</h2>
                <p className="seo-body">
                  Contractor management software helps businesses organise the
                  information, documents, responsibilities and compliance activities
                  associated with contractors and workers.
                </p>
                <p className="seo-body">
                  Instead of managing contractor information across separate
                  spreadsheets, emails and folders, Complimate connects contractor
                  and worker compliance with the wider WHS management system.
                </p>
                <p className="seo-body">
                  This gives compliance teams and managers a more structured way to
                  manage contractor-related records and follow-up tasks.
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section cms-help-section">
          <Container>
            <div className="seo-section-header cms-centered-heading">
              <h2 className="seo-h2">
                How Does Complimate Help With Contractor Management?
              </h2>
              <p className="seo-body">
                Complimate connects contractor management with the other parts of
                your compliance workflow.
              </p>
              <p className="seo-body">You can manage areas such as:</p>
            </div>
            <ul className="cms-capability-grid">
              {HELP_AREAS.map((item, index) => (
                <li key={item} className="cms-capability-card">
                  <FeatureIcon name={HELP_ICONS[index]} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="seo-body cms-section-note">
              These capabilities are part of the broader Complimate platform and
              are documented as current product functionality.
            </p>
          </Container>
        </section>

        <section className="seo-section cms-features-section">
          <Container>
            <div className="seo-section-header cms-centered-heading">
              <h2 className="seo-h2">What Contractor Compliance Can You Manage?</h2>
            </div>
            <div className="cms-feature-grid">
              {COMPLIANCE_FEATURES.map((item, index) => (
                <article key={item.title} className="cms-feature-card">
                  <div className="cms-card-topline">
                    <FeatureIcon name={FEATURE_ICONS[index]} />
                    <span aria-hidden="true">0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph, paragraphIndex) => (
                    <p key={`${item.title}-${paragraphIndex}`} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section cms-cta-section">
          <Container>
            <div className="cms-cta-card">
              <div className="cms-cta-copy">
                <h2 className="seo-h2">Manage Contractor Compliance in One Place</h2>
                <p className="seo-body">
                  Bring contractor records, documents, forms, site information and
                  compliance workflows together with Complimate contractor
                  management software.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us"
                className="btn-teal cms-cta-button"
                id="cms_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
              <span className="cms-cta-orb cms-cta-orb-one" aria-hidden="true" />
              <span className="cms-cta-orb cms-cta-orb-two" aria-hidden="true" />
            </div>
          </Container>
        </section>

        <section className="seo-section cms-workflow-section">
          <Container>
            <div className="cms-workflow-grid">
              <div>
                <div className="cms-section-marker"><FeatureIcon name="task" /></div>
                <h2 className="seo-h2">
                  How Does Contractor Management Connect With WHS?
                </h2>
                <p className="seo-body">
                  Contractor management is only one part of a wider WHS management
                  system.
                </p>
                <p className="seo-body">
                  For example, a contractor-related compliance issue may require
                  more than simply recording a document. Your team may need to
                  identify an issue, assign responsibility, create a task, attach
                  evidence and track the action through to completion.
                </p>
                <p className="seo-body">
                  Complimate is designed to connect these activities. Its project
                  and task functionality supports assigned tasks, priorities, due
                  dates, attachments, comments and evidence, while audit findings
                  can be turned into trackable corrective actions.
                </p>
                <p className="seo-body">This creates a workflow such as:</p>
              </div>
              <div className="cms-workflow" aria-label="Contractor compliance workflow">
                {['Contractor activity', 'Compliance issue', 'Assigned action', 'Evidence', 'Completion'].map((step, index) => (
                  <div className="cms-workflow-step" key={step}>
                    <span className="cms-workflow-number">{index + 1}</span>
                    <strong>{step}</strong>
                    {index < 4 && <span className="cms-workflow-arrow" aria-hidden="true">↓</span>}
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section cms-audience-section">
          <Container>
            <div className="cms-audience-card">
              <div className="cms-audience-copy">
                <div className="cms-section-marker"><FeatureIcon name="users" /></div>
                <h2 className="seo-h2">
                  Who Is Complimate Contractor Management Software For?
                </h2>
                <p className="seo-body">
                  Complimate is designed for Australian businesses that need to
                  manage contractor and worker compliance as part of a broader WHS
                  system.
                </p>
                <p className="seo-body">It can be relevant to:</p>
                <p className="seo-body">
                  The platform is particularly positioned for small and medium
                  businesses while supporting a complete WHS management approach.
                </p>
              </div>
              <ul className="cms-audience-list">
                {AUDIENCE.map((item) => (
                  <li key={item}>
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Container>
        </section>

        <section className="seo-section cms-faq-section" id="faq">
          <Container>
            <div className="seo-section-header cms-centered-heading">
              <h2 className="seo-h2">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h3">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default ContractorManagementSoftware;
